import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { StorageAdapter, StorageAdapterEntityName } from '../StorageAdapter/StorageAdapter';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
export class OperationExecutor<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> {
  /**
   * @param operations
   * @returns an array of failed operations
   */
  public async executeOperations(operations: Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>): Promise<Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>> {
    const bulkOperations = this.bundleOperations(operations);
    const failedBulkOperations: Array<BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = [];
    for (const bulkOperation of bulkOperations) {
      try {
        await this.executeBulkOperation(bulkOperation);
      } catch (e) {
        // logging for debugging purposes
        // eslint-disable-next-line no-console
        console.error(e);
        failedBulkOperations.push(bulkOperation);
      }
    }
    return this.unbundleBulkOperations(failedBulkOperations);
  }
  private bundleOperations(operations: Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>): Array<BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
    const bulkOperations: Array<BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = [];
    for (const operation of operations) {
      const bulkOperation = bulkOperations.find(bo => bo.entityName === operation.entityName && bo.type === operation.type);
      if (bulkOperation) {
        this.mergeOperationIntoBulkOperation(operation, bulkOperation);
      } else {
        bulkOperations.push(this.createBulkOperation(operation));
      }
    }
    return bulkOperations;
  }
  private mergeOperationIntoBulkOperation(operation: Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>, bulkOperation: BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): void {
    switch (bulkOperation.type) {
      case OperationType.DELETE:
        if (operation.type !== OperationType.DELETE) {
          throw new Error(`wrong operationType "${operation.type}"`);
        }
        bulkOperation.entityIds.push(operation.entityId);
        break;
      case OperationType.SAVE:
        if (operation.type !== OperationType.SAVE) {
          throw new Error(`wrong operationType "${operation.type}"`);
        }
        bulkOperation.entities.push(operation.entity);
        break;
      default:
        throw new Error(`unkown operation type "${(bulkOperation as BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>).type}"`);
    }
  }
  private createBulkOperation(operation: Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName> {
    switch (operation.type) {
      case OperationType.DELETE:
        return {
          type: operation.type,
          entityIds: [operation.entityId],
          entityName: operation.entityName
        };
      case OperationType.SAVE:
        return {
          type: operation.type,
          entities: [operation.entity],
          entityName: operation.entityName
        };
      default:
        throw new Error(`unkown operation type "${(operation as Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>).type}"`);
    }
  }
  private unbundleBulkOperations(bulkOperations: Array<BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>): Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
    const operations: Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = [];
    for (const bulkOperation of bulkOperations) {
      switch (bulkOperation.type) {
        case OperationType.DELETE:
          operations.push(...this.unbundleDeleteBulkOperation(bulkOperation));
          break;
        case OperationType.SAVE:
          operations.push(...this.unbundleSaveBulkOperation(bulkOperation));
          break;
        default:
          throw new Error(`unkown operation type "${(bulkOperation as BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>).type}"`);
      }
    }
    return operations;
  }
  private unbundleDeleteBulkOperation(bulkOperation: BulkDeleteOperation<TSynchronizationEnvironmentTypes>): Array<DeleteOperation<TSynchronizationEnvironmentTypes>> {
    return bulkOperation.entityIds.map(entityId => ({
      entityId,
      entityName: bulkOperation.entityName,
      type: bulkOperation.type
    }));
  }
  private unbundleSaveBulkOperation(bulkOperation: BulkSaveOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): Array<SaveOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
    return bulkOperation.entities.map(entity => ({
      entity,
      entityId: entity.id,
      entityName: bulkOperation.entityName,
      type: bulkOperation.type
    }));
  }
  private async executeBulkOperation(bulkOperation: BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): Promise<void> {
    switch (bulkOperation.type) {
      case OperationType.DELETE:
        await this.executeBulkDelete(bulkOperation.entityName, bulkOperation.entityIds);
        break;
      case OperationType.SAVE:
        await this.executeBulkSave(bulkOperation.entityName, bulkOperation.entities);
        break;
      default:
        throw new Error(`unkown operation type "${(bulkOperation as BulkOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>).type}"`);
    }
  }
  private async executeBulkDelete(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], entityIds: Array<EntityManagerBaseEntity['id']>): Promise<void> {
    await StorageAdapter.removeEntities(entityIds, entityName as StorageAdapterEntityName);
  }
  private async executeBulkSave(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], entities: Array<TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']>): Promise<void> {
    await StorageAdapter.setEntities(entities, entityName as StorageAdapterEntityName);
  }
}
export enum OperationType {
  DELETE = 'delete',
  SAVE = 'save',
}
export type OperationBase<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = {
  entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
  type: OperationType;
  entityId: EntityManagerBaseEntity['id'];
};
export type DeleteOperation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = OperationBase<TSynchronizationEnvironmentTypes> & {
  type: OperationType.DELETE;
};
export type SaveOperation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = OperationBase<TSynchronizationEnvironmentTypes> & {
  type: OperationType.SAVE;
  entity: TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity'];
};
export type Operation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = DeleteOperation<TSynchronizationEnvironmentTypes> | SaveOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>;
type BulkOperationBase<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = {
  type: OperationType;
  entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];
};
type BulkDeleteOperation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint> = BulkOperationBase<TSynchronizationEnvironmentTypes> & {
  type: OperationType.DELETE;
  entityIds: Array<EntityManagerBaseEntity['id']>;
};
type BulkSaveOperation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = BulkOperationBase<TSynchronizationEnvironmentTypes> & {
  type: OperationType.SAVE;
  entities: Array<TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']>;
};
type BulkOperation<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = BulkDeleteOperation<TSynchronizationEnvironmentTypes> | BulkSaveOperation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>;